import { useState } from "react";
import { useTheme, useAuth } from "@/App";
import api from "@/lib/api";
import OnboardingBanner from "@/components/OnboardingBanner";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import ChannelMixCard from "@/components/ChannelMixCard";
import PricingAlertsCard from "@/components/PricingAlertsCard";
import { useDashboardStats, useDashboardComparison } from "@/lib/queries";
import { usePropertyFilter } from "@/context/PropertyFilter";
import { formatINR } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { TrendUp, TrendDown, CalendarBlank, Bell, Buildings, CurrencyInr, ChartBar, FilePdf, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const PROP_COLORS = ["#E8B931", "#2DD4BF", "#60A5FA", "#FB7185", "#A78BFA", "#F59E0B", "#34D399", "#F472B6"];

export default function Dashboard() {
  const { user } = useAuth();
  // Revenue visibility is the host's call per team member (2026-09-07); backend zeroes the figures too
  const hideRevenue = !!(user?.is_staff && !user?.show_revenue);
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { selectedPropertyId, selectedRoomId } = usePropertyFilter();
  const [period, setPeriod] = useState("monthly");  // "monthly" | "yearly"
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear]   = useState(() => new Date().getFullYear());
  const [pnlMonth, setPnlMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const apiPeriod = period === "yearly" ? "year" : "month";
  const { data: stats, isLoading: statsLoading } = useDashboardStats(
    apiPeriod,
    period === "monthly" ? selectedMonth : null,
    period === "yearly"  ? selectedYear  : null,
    selectedPropertyId || null,
    selectedRoomId || null,
  );
  const { data: comparison = [], isLoading: compLoading } = useDashboardComparison(
    apiPeriod,
    period === "monthly" ? selectedMonth : null,
    period === "yearly"  ? selectedYear  : null,
    selectedPropertyId || null,
  );
  const loading = statsLoading || compLoading;

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const downloadPnl = (propId) => {
    const params = new URLSearchParams({ format: "pdf", month: pnlMonth });
    if (propId && propId !== "all") params.append("property_id", propId);
    window.open(`${api.defaults.baseURL}/export/pnl?${params}`, "_blank");
    toast.success("Generating P&L report...");
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="dashboard-loading">
        <div className="h-8 w-48 bg-slate-200 rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-slate-100 rounded-2xl" />)}
        </div>
        <div className="h-72 bg-slate-100 rounded-2xl" />
      </div>
    );
  }

  const kpis = [
    { title: "Revenue", value: formatINR(stats?.revenue || 0), icon: CurrencyInr, color: "text-amber-500", bg: "bg-amber-50", accent: "kpi-gold" },
    { title: "Occupancy", value: `${stats?.occupancy || 0}%`, icon: Buildings, color: "text-teal-500", bg: "bg-teal-50", accent: "kpi-teal" },
    { title: "ADR", value: formatINR(stats?.adr || 0), icon: TrendUp, color: "text-blue-500", bg: "bg-blue-50", accent: "kpi-blue" },
    { title: "Net Profit", value: formatINR(stats?.net_profit || 0), icon: (stats?.net_profit || 0) >= 0 ? TrendUp : TrendDown, color: (stats?.net_profit || 0) >= 0 ? "text-amber-500" : "text-red-500", bg: (stats?.net_profit || 0) >= 0 ? "bg-amber-50" : "bg-red-50", accent: (stats?.net_profit || 0) >= 0 ? "kpi-gold" : "kpi-coral" },
  ].filter(k => !hideRevenue || !["Revenue", "ADR", "Net Profit"].includes(k.title));

  const chartData = period === "yearly" ? aggregateYearly(stats?.chart) : stats?.chart;

  const chartGrid   = isDark ? "#334155" : "#f1f5f9";
  const chartAxis   = isDark ? "#94a3b8" : "#94a3b8";
  const tooltipStyle = isDark
    ? { backgroundColor: "#1e293b", border: "1px solid #334155", borderRadius: "12px", fontSize: "12px", color: "#f1f5f9", boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }
    : { backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", fontSize: "12px", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" };

  return (
    <div className="space-y-6" data-testid="dashboard">
      <OnboardingBanner pageKey="dashboard" />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-manrope text-2xl md:text-3xl font-semibold text-slate-900 dark:text-amber-400 tracking-tight">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Overview of your property portfolio</p>
        </div>
        {/* Period selector — controls KPI cards */}
        <div className="flex items-center gap-2 flex-wrap">
          <Tabs value={period} onValueChange={setPeriod}>
            <TabsList className="bg-slate-100 dark:bg-slate-800 h-8">
              <TabsTrigger value="monthly" className="text-xs h-6 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Monthly</TabsTrigger>
              <TabsTrigger value="yearly"  className="text-xs h-6 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Yearly</TabsTrigger>
            </TabsList>
          </Tabs>
          {period === "monthly" && (
            <input
              type="month"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-slate-800 dark:text-slate-100 text-xs focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all h-8"
            />
          )}
          {period === "yearly" && (
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-slate-800 dark:text-slate-100 text-xs focus:border-amber-400 outline-none transition-all h-8"
            >
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 ambient-glow" data-testid="kpi-cards">
        {kpis.map((kpi) => {
          // Scale font size down for long rupee values so they never overflow the card
          const vLen = kpi.value.length;
          const numCls = vLen > 12
            ? "font-mono text-base md:text-lg font-extrabold mt-1 truncate"
            : vLen > 9
            ? "font-mono text-lg md:text-2xl font-extrabold mt-1"
            : "font-mono text-xl md:text-3xl font-extrabold mt-1";
          return (
          <Card key={kpi.title} className={`bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-900 border-slate-200 dark:border-slate-700 rounded-2xl card-hover shadow-sm ${kpi.accent}`}>
            <CardContent className="p-4 md:p-6 relative z-10 overflow-hidden">
              <div className={`inline-flex p-2.5 rounded-xl ${kpi.bg} mb-3`}>
                <kpi.icon size={20} className={kpi.color} weight="duotone" />
              </div>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-slate-500 dark:text-slate-300 font-semibold">{kpi.title}</p>
              <p
                className={`${numCls} ${kpi.color}`}
                title={kpi.value}
                data-testid={`kpi-${kpi.title.toLowerCase().replace(' ', '-')}`}
              >
                {kpi.value}
              </p>
            </CardContent>
          </Card>
          );
        })}
      </div>

      <OnboardingChecklist />

      {/* Open pricing nudges: gap-fill / low-occupancy first. Renders nothing when there are none. */}
      <PricingAlertsCard />

      <ChannelMixCard />

      {/* Revenue vs Expenses Chart */}
      {!hideRevenue && (
      <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100">Revenue vs Expenses</CardTitle>
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {period === "yearly" ? `${selectedYear} — Full Year` : `Last 12 months`}
          </span>
        </CardHeader>
        <CardContent className="p-2 md:p-4" data-testid="revenue-chart">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
              <XAxis dataKey={period === "yearly" ? "year" : "month"} stroke={chartAxis} fontSize={11} tickLine={false} />
              <YAxis stroke={chartAxis} fontSize={11} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: isDark ? "#f1f5f9" : "#0f172a" }} formatter={(value) => [formatINR(value)]} />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Bar dataKey="revenue" name="Revenue" fill="#E8B931" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill={isDark ? "#475569" : "#94a3b8"} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      )}

      {/* Multi-Property Comparison */}
      {!hideRevenue && comparison.length > 1 && (
        <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl card-glow shadow-sm" data-testid="property-comparison">
          <CardHeader className="pb-2">
            <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <ChartBar size={18} className="text-blue-500" weight="duotone" />
              Property Comparison
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-4">
            <Tabs defaultValue="revenue">
              <TabsList className="bg-slate-100 h-8 mb-4">
                <TabsTrigger value="revenue" className="text-xs h-6 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Revenue</TabsTrigger>
                <TabsTrigger value="profit" className="text-xs h-6 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Profit</TabsTrigger>
                <TabsTrigger value="overview" className="text-xs h-6 data-[state=active]:bg-amber-400 data-[state=active]:text-white">Overview</TabsTrigger>
              </TabsList>
              <TabsContent value="revenue">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={comparison} layout="vertical" barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} horizontal={false} />
                    <XAxis type="number" stroke={chartAxis} fontSize={10} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="name" stroke={chartAxis} fontSize={11} tickLine={false} width={100} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [formatINR(v)]} />
                    <Bar dataKey="revenue" name="Revenue" radius={[0, 6, 6, 0]}>
                      {comparison.map((entry, i) => (
                        <Cell key={i} fill={PROP_COLORS[i % PROP_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </TabsContent>
              <TabsContent value="profit">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={comparison} layout="vertical" barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} horizontal={false} />
                    <XAxis type="number" stroke={chartAxis} fontSize={10} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <YAxis type="category" dataKey="name" stroke={chartAxis} fontSize={11} tickLine={false} width={100} />
                    <Tooltip contentStyle={tooltipStyle} formatter={v => [formatINR(v)]} />
                    <Bar dataKey="profit" name="Net Profit" radius={[0, 6, 6, 0]}>
                      {comparison.map((entry, i) => (
                        <Cell key={i} fill={entry.profit >= 0 ? "#E8B931" : "#FB7185"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </TabsContent>
              <TabsContent value="overview">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {comparison.map((prop, i) => (
                    <div key={prop.id} className="surface-raised rounded-xl p-4 border border-slate-200" style={{ borderLeftColor: PROP_COLORS[i % PROP_COLORS.length], borderLeftWidth: 3 }}>
                      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{prop.name}</p>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Revenue</p>
                          <p className="font-mono text-amber-500 font-medium">{formatINR(prop.revenue)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Expenses</p>
                          <p className="font-mono text-red-400 font-medium">{formatINR(prop.expenses)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Profit</p>
                          <p className={`font-mono font-medium ${prop.profit >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>{formatINR(prop.profit)}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 dark:text-slate-400">Bookings</p>
                          <p className="font-mono text-slate-700 dark:text-slate-200 font-medium">{prop.bookings}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* P&L Generator */}
      {!hideRevenue && (
      <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl card-glow shadow-sm" data-testid="pnl-generator">
        <CardHeader className="pb-3">
          <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <FilePdf size={18} className="text-red-400" weight="duotone" />
            P&L Statement Generator
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Generate a monthly Profit & Loss statement for your CA. Includes revenue, expenses by category, EMI, GST-eligible spend, claimable GST (ITC), and tax deductibles.</p>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 font-semibold">Month</p>
              <input
                type="month"
                value={pnlMonth}
                onChange={e => setPnlMonth(e.target.value)}
                className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-slate-800 dark:text-slate-100 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-all"
                data-testid="pnl-month-input"
              />
            </div>
            <button
              onClick={() => downloadPnl("all")}
              className="bg-gradient-to-r from-amber-400 to-amber-500 text-white hover:brightness-105 font-semibold rounded-xl px-5 py-2.5 text-sm flex items-center gap-2 transition-all shadow-sm"
              data-testid="download-pnl-button"
            >
              <DownloadSimple size={16} weight="bold" /> Download All Properties
            </button>
            {stats?.properties?.length > 0 && stats.properties.map(p => (
              <button
                key={p.id}
                onClick={() => downloadPnl(p.id)}
                className="surface-raised border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium rounded-xl px-4 py-2.5 text-xs flex items-center gap-1.5 transition-colors"
                data-testid={`pnl-${p.id}`}
              >
                <FilePdf size={14} /> {p.name}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Property Status */}
        <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Buildings size={18} className="text-amber-500" weight="duotone" />
              Property Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="property-status">
            {stats?.properties?.length ? stats.properties.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 surface-inset rounded-xl border border-slate-100 dark:border-slate-700">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 dark:text-slate-100 font-medium truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{p.city || p.address}</p>
                </div>
                <Badge variant="outline" className={
                  p.status === "active" ? "bg-emerald-50 text-emerald-600 border-emerald-200" :
                  p.status === "maintenance" ? "bg-amber-50 text-amber-600 border-amber-200" :
                  "bg-slate-100 text-slate-300 border-slate-200"
                }>
                  {p.status}
                </Badge>
              </div>
            )) : (
              <p className="text-slate-400 text-sm text-center py-6">No properties yet. Add your first property!</p>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Bookings */}
        <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <CalendarBlank size={18} className="text-blue-500" weight="duotone" />
              Upcoming Bookings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="upcoming-bookings">
            {stats?.upcoming_bookings?.length ? stats.upcoming_bookings.slice(0, 5).map((b) => (
              <div key={b.id} className="flex items-center justify-between p-3 surface-inset rounded-xl border border-slate-100 dark:border-slate-700">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-800 dark:text-slate-100 font-medium truncate">{b.guest_name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{b.check_in} to {b.check_out}</p>
                </div>
                <span className="font-mono text-sm text-amber-500 font-semibold ml-2">{formatINR(b.amount)}</span>
              </div>
            )) : (
              <p className="text-slate-400 text-sm text-center py-6">No upcoming bookings</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reminders */}
      {stats?.reminders?.length > 0 && (
        <Card className="surface-card border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="font-manrope text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <Bell size={18} className="text-amber-400" weight="duotone" />
              Reminders & Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="reminders">
            {stats.reminders.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-3 surface-inset rounded-xl border border-slate-100 dark:border-slate-700">
                <div className={`w-2 h-2 rounded-full shrink-0 status-pulse ${
                  r.type === "emi" ? "bg-red-400" :
                  r.type === "tax" ? "bg-amber-400" :
                  r.type === "maintenance" ? "bg-blue-400" : "bg-emerald-400"
                }`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 dark:text-slate-100 font-medium truncate">{r.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{r.due_date}</p>
                </div>
                <Badge variant="outline" className="bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 text-[10px] capitalize">{r.type}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function aggregateYearly(chart) {
  if (!chart) return [];
  const years = {};
  chart.forEach(d => {
    if (!years[d.year]) years[d.year] = { year: d.year, revenue: 0, expenses: 0 };
    years[d.year].revenue += d.revenue;
    years[d.year].expenses += d.expenses;
  });
  return Object.values(years);
}
